import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminInvoiceService } from '../../services/adminInvoiceService';

// Async thunks for admin invoice operations
export const fetchAdminInvoices = createAsyncThunk(
  'adminInvoices/fetchAdminInvoices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminInvoiceService.getAllInvoices();
      return response.invoices || response.data || response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch invoices');
    }
  }
);

export const fetchAdminInvoiceDetails = createAsyncThunk(
  'adminInvoices/fetchAdminInvoiceDetails',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await adminInvoiceService.getInvoiceDetails(invoiceId);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch invoice details');
    }
  }
);

export const downloadAdminInvoicePDF = createAsyncThunk(
  'adminInvoices/downloadAdminInvoicePDF',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const blob = await adminInvoiceService.downloadInvoicePDF(invoiceId);
      return { invoiceId, blob };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message || 'Failed to download invoice');
    }
  }
);

export const sendInvoiceReminder = createAsyncThunk(
  'adminInvoices/sendInvoiceReminder',
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await adminInvoiceService.sendReminder(invoiceId);
      return { invoiceId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message || 'Failed to send reminder');
    }
  }
);

export const deleteAdminInvoice = createAsyncThunk(
  "adminInvoices/deleteAdminInvoice",
  async (invoiceId, { rejectWithValue }) => {
    try {
      const response = await adminInvoiceService.deleteInvoice(invoiceId);
      return { invoiceId, ...response };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || "Failed to delete invoice"
      );
    }
  }
);

const adminInvoiceSlice = createSlice({
  name: 'adminInvoices',
  initialState: {
    invoices: [],
    selectedInvoice: null,
    loading: false,
    error: null,
    detailsLoading: false,
    detailsError: null,
    downloadLoading: null, // Track which invoice is being downloaded
    downloadError: null,
    reminderLoading: null, // Track which invoice reminder is being sent
    reminderError: null,
    deleteLoading: null, // Track which invoice is being deleted
    deleteError: null,
    filters: {
      searchTerm: '',
      statusFilter: 'all',
      dateFilter: 'all',
    },
    pagination: {
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0,
    },
  },
  reducers: {
    clearErrors: (state) => {
      state.error = null;
      state.detailsError = null;
      state.downloadError = null;
      state.reminderError = null;
      state.deleteError = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1; // Reset to first page when filters change
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    clearSelectedInvoice: (state) => {
      state.selectedInvoice = null;
      state.detailsError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all invoices
      .addCase(fetchAdminInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = action.payload;
        state.pagination.totalItems = action.payload.length;
      })
      .addCase(fetchAdminInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch invoice details
      .addCase(fetchAdminInvoiceDetails.pending, (state) => {
        state.detailsLoading = true;
        state.detailsError = null;
      })
      .addCase(fetchAdminInvoiceDetails.fulfilled, (state, action) => {
        state.detailsLoading = false;
        state.selectedInvoice = action.payload;
      })
      .addCase(fetchAdminInvoiceDetails.rejected, (state, action) => {
        state.detailsLoading = false;
        state.detailsError = action.payload;
      })
      // Download invoice PDF
      .addCase(downloadAdminInvoicePDF.pending, (state, action) => {
        state.downloadLoading = action.meta.arg; // invoiceId
        state.downloadError = null;
      })
      .addCase(downloadAdminInvoicePDF.fulfilled, (state) => {
        state.downloadLoading = null;
      })
      .addCase(downloadAdminInvoicePDF.rejected, (state, action) => {
        state.downloadLoading = null;
        state.downloadError = action.payload;
      })
      // Send reminder
      .addCase(sendInvoiceReminder.pending, (state, action) => {
        state.reminderLoading = action.meta.arg; // invoiceId
        state.reminderError = null;
      })
      .addCase(sendInvoiceReminder.fulfilled, (state) => {
        state.reminderLoading = null;
      })
      .addCase(sendInvoiceReminder.rejected, (state, action) => {
        state.reminderLoading = null;
        state.reminderError = action.payload;
      })
      // Delete invoice
      .addCase(deleteAdminInvoice.pending, (state, action) => {
        state.deleteLoading = action.meta.arg; // invoiceId
        state.deleteError = null;
      })
      .addCase(deleteAdminInvoice.fulfilled, (state, action) => {
        state.deleteLoading = null;
        state.invoices = state.invoices.filter(
          (i) => i.id !== action.payload.invoiceId
        );
        state.pagination.totalItems = state.invoices.length;
      })
      .addCase(deleteAdminInvoice.rejected, (state, action) => {
        state.deleteLoading = null;
        state.deleteError = action.payload;
      });
  },
});

export const { clearErrors, setFilters, setPagination, clearSelectedInvoice } = adminInvoiceSlice.actions;

// Selectors
export const selectFilteredInvoices = (state) => {
  const { invoices, filters } = state.adminInvoices;
  const { searchTerm, statusFilter, dateFilter } = filters;

  return invoices.filter(invoice => {
    const matchesSearch = !searchTerm || 
      invoice.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invoice.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || invoice.status === statusFilter;
    
    // Add date filtering logic here if needed
    const matchesDate = dateFilter === 'all'; // Simplified for now
    
    return matchesSearch && matchesStatus && matchesDate;
  });
};

export const selectPaginatedInvoices = (state) => {
  const filteredInvoices = selectFilteredInvoices(state);
  const { currentPage, itemsPerPage } = state.adminInvoices.pagination;
  
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  return filteredInvoices.slice(startIndex, endIndex);
};

export default adminInvoiceSlice.reducer;